import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  getDepartments,
  getExhibitionSettings,
  getOccupationalGroups,
  getOperatorsSettings,
  getProfessions,
  getSelectedExhibitionId,
  getTitles,
  selectIsGoogleMapsApiEnabled
} from '@app/app.reducer';
import {
  DynamicFormField,
  DynamicFormFields,
  FieldKey
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormFactory } from '@core/data-access/models/form/form-mediator.model';
import { FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { DynamicFormFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-factory.service';
import { DynamicFormFieldBuilder } from '@core/data-access/services/dynamic-form-field/dynamic-form-field-builder.service';
import { SavedTicketHolderFormStates } from '@core/features/account/features/saved-ticket-holders/models/saved-ticket-holders-fom.model';
import { SavedTicketHolders } from '@core/features/account/features/saved-ticket-holders/models/saved-ticket-holders.store.model';
import { selectSavedTicketHoldersForms } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.selectors';
import { BuyerUuid } from '@core/features/buyer-information/models/buyer-information-store.model';
import { isFieldHidden } from '@core/utils/dynamic-form-field/dynamic-form-field-helpers';
import { savedTicketHolderFormDynamicFormFieldParametersMapper } from '@core/utils/dynamic-form-field/dynamic-form-field-parameters-mapper';
import { setChangedFormFields } from '@core/utils/form-fields/form-field-pipes';
import { select, Store } from '@ngrx/store';
import { HolderUuid, HolderUuids } from '@products/models/holder.model';
import {
  ExhibitionSettingModel,
  OperatorsSettingsModel
} from '@shared/services-with-reducers/customization/customization.interfaces';
import { SelectOption } from '@shared/services-with-reducers/exhibition/exhibition.interface';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import {
  filter as _filter,
  reduce as _reduce,
  difference,
  forEach,
  forIn,
  has,
  includes,
  isEmpty,
  isEqual,
  isNull,
  keys,
  sortBy
} from 'lodash';
import {
  combineLatest,
  concatMap,
  debounceTime,
  filter,
  first,
  from,
  iif,
  map,
  merge,
  Observable,
  of,
  pairwise,
  pipe,
  reduce,
  startWith,
  switchMap,
  tap
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SavedTicketHolderFormsFactory implements FormFactory {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #dynamicFormFieldBuilder = inject(DynamicFormFieldBuilder);
  readonly #dynamicFormFactory = inject(DynamicFormFactory);
  readonly #dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory);

  readonly #initialState: SavedTicketHolderFormStates = new Map();

  readonly #savedTicketHoldersForms$ = this.#store.select(selectSavedTicketHoldersForms);

  readonly #isFormStateCreated = signal(false);

  readonly state = toSignal(this.state$(), { initialValue: this.#initialState });

  resetFormState() {}

  private state$(): Observable<SavedTicketHolderFormStates> {
    return merge(this.setFormState$(), this.updateFormState$()).pipe(takeUntilDestroyed(this.#destroyRef));
  }

  private setFormState$() {
    const settings$ = combineLatest({
      operatorsSettings: this.#store.select(getOperatorsSettings),
      exhibitionSettings: this.#store.select(getExhibitionSettings)
    }).pipe(
      debounceTime(50),
      filter(({ operatorsSettings }) => !isEmpty(operatorsSettings)),
      map(({ operatorsSettings, exhibitionSettings }) =>
        !isEmpty(exhibitionSettings) ? exhibitionSettings : operatorsSettings
      )
    );

    const isTranslationLoaded$ = this.#store.pipe(
      select(getIsTranslationLoaded),
      filter((isTranslationLoaded) => isTranslationLoaded)
    );

    return combineLatest({
      settings: settings$,
      isTranslationLoaded: isTranslationLoaded$,
      savedTicketHoldersForms: this.#savedTicketHoldersForms$
    }).pipe(
      startWith({ settings: null, isTranslationLoaded: false, savedTicketHoldersForms: null }),
      pairwise(),
      filter(
        ([previousChanges, currentChanges]) =>
          !isEqual(
            keys(previousChanges.savedTicketHoldersForms).length,
            keys(currentChanges.savedTicketHoldersForms).length
          ) ||
          (!isEmpty(previousChanges.settings) &&
            !isEqual(previousChanges.settings?.eventId, currentChanges.settings?.eventId))
      ),
      map(([previousChanges, currentChanges]) => {
        const previousHolderUuids = keys(previousChanges.savedTicketHoldersForms);
        const currentHolderUuids = keys(currentChanges.savedTicketHoldersForms);

        const isSettingsChanged = !isEqual(previousChanges.settings?.eventId, currentChanges.settings?.eventId);

        const createAddOrRecreateFormToFormState =
          previousHolderUuids.length < currentHolderUuids.length || isSettingsChanged;

        const holderUuidDifference = createAddOrRecreateFormToFormState
          ? difference(currentHolderUuids, previousHolderUuids)
          : difference(previousHolderUuids, currentHolderUuids);

        const createAddOrRecreateHolderUuids = isSettingsChanged ? currentHolderUuids : holderUuidDifference;

        return {
          settings: currentChanges.settings,
          ticketHolderForms: currentChanges.savedTicketHoldersForms,
          createAddOrRecreateFormToFormState,
          createAddOrRecreateHolderUuids,
          isSettingsChanged
        };
      }),
      switchMap(
        ({
          createAddOrRecreateFormToFormState,
          settings,
          ticketHolderForms,
          createAddOrRecreateHolderUuids,
          isSettingsChanged
        }) =>
          iif(
            () => createAddOrRecreateFormToFormState,
            this.createAddOrRecreateFormToFormState$(
              settings,
              ticketHolderForms,
              createAddOrRecreateHolderUuids,
              isSettingsChanged
            ),
            this.removeFormFromFormStates$(createAddOrRecreateHolderUuids)
          )
      )
    );
  }

  private createAddOrRecreateFormToFormState$(
    settings: OperatorsSettingsModel | ExhibitionSettingModel,
    savedTicketHolderForms: SavedTicketHolders,
    createAddOrRecreateHolderUuids: HolderUuids,
    isSettingsChanged: boolean
  ) {
    return iif(
      () => !this.#isFormStateCreated() || isSettingsChanged,
      this.createFormStates$(settings, savedTicketHolderForms, createAddOrRecreateHolderUuids),
      this.addFormStateToFormStates$(settings, savedTicketHolderForms, createAddOrRecreateHolderUuids)
    );
  }

  private createFormStates$(
    settings: OperatorsSettingsModel | ExhibitionSettingModel,
    savedTicketHolderForms: SavedTicketHolders,
    createOrRecreateFormHolderUuids: HolderUuids
  ) {
    return from(createOrRecreateFormHolderUuids).pipe(
      this.createFormSatePipe(settings, savedTicketHolderForms),
      tap(() => this.#isFormStateCreated.set(true))
    );
  }

  private addFormStateToFormStates$(
    settings: OperatorsSettingsModel | ExhibitionSettingModel,
    savedTicketHolderForms: SavedTicketHolders,
    addFormHolderUuids: HolderUuids
  ) {
    return from(addFormHolderUuids).pipe(
      this.createFormSatePipe(settings, savedTicketHolderForms),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private createFormSatePipe(
    settings: OperatorsSettingsModel | ExhibitionSettingModel,
    savedTicketHolderForms: SavedTicketHolders
  ) {
    return pipe(
      map((savedHolderUuid: BuyerUuid | HolderUuid) => ({
        person: savedTicketHolderForms[savedHolderUuid].person,
        formFields: savedTicketHolderForms[savedHolderUuid]?.formFields || {}
      })),
      concatMap(({ person, formFields }) =>
        this.createSavedTicketHolderDynamicFormFields$(settings, formFields).pipe(
          tap((dynamicFormFields) =>
            this.#dynamicFieldOptionsTranslationFactory.createTranslatedDynamicFieldOptions(
              FormName.SavedTicketHolders,
              dynamicFormFields
            )
          ),
          map((dynamicFormFields) => ({
            dynamicFormFields,
            form: {
              formName: FormName.SavedTicketHolders,
              formGroup: this.#dynamicFormFactory.createForm(dynamicFormFields, formFields)
            },
            person
          }))
        )
      ),
      reduce((formStates: SavedTicketHolderFormStates, formState) => {
        const { dynamicFormFields, form, person } = formState;

        formStates.set(person.uuid, { dynamicFormFields, form, person });

        return formStates;
      }, new Map())
    );
  }

  private removeFormFromFormStates$(removeSavedHolderUuids: HolderUuids) {
    return of(removeSavedHolderUuids).pipe(
      filter(() => this.#isFormStateCreated()),
      map(
        (removeFormTariffUuids) => new Map(_filter([...this.state()], ([key]) => !includes(removeFormTariffUuids, key)))
      )
    );
  }

  private updateFormState$() {
    return toObservable(this.#isFormStateCreated).pipe(
      filter((isFormStateCreated) => isFormStateCreated),
      switchMap(() => merge(this.updateFormStateOnSetSavedTicketHolderForms$()))
    );
  }

  private updateFormStateOnSetSavedTicketHolderForms$() {
    return this.#savedTicketHoldersForms$.pipe(
      pairwise(),
      filter(
        ([previousSavedTicketHolderFormChanges, currentSavedTicketHolderFormChanges]) =>
          !isEmpty(previousSavedTicketHolderFormChanges) &&
          !isEmpty(currentSavedTicketHolderFormChanges) &&
          isEqual(keys(previousSavedTicketHolderFormChanges).length, keys(currentSavedTicketHolderFormChanges).length)
      ),
      map(([previousSavedTicketHolderFormChanges, currentSavedTicketHolderFormChanges]) =>
        _reduce(
          keys(currentSavedTicketHolderFormChanges),
          (changedFormStates: SavedTicketHolderFormStates, savedHolderUuid) => {
            const changedFormFieldsForm = setChangedFormFields([
              previousSavedTicketHolderFormChanges[savedHolderUuid].formFields,
              currentSavedTicketHolderFormChanges[savedHolderUuid].formFields
            ]);

            if (isEmpty(changedFormFieldsForm)) {
              return changedFormStates;
            }

            const { dynamicFormFields, form, person } = this.state()?.get(savedHolderUuid);

            const updatedTicketHolderFormState = {
              dynamicFormFields: this.#dynamicFormFactory.updateForm(
                dynamicFormFields,
                changedFormFieldsForm,
                form.formGroup
              ),
              form,
              person
            };

            changedFormStates.set(savedHolderUuid, updatedTicketHolderFormState);

            return changedFormStates;
          },
          new Map()
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private createSavedTicketHolderDynamicFormFields$(
    settings: OperatorsSettingsModel | ExhibitionSettingModel,
    formFields: FormFields
  ) {
    const waitForSetOrResetEvenFieldOptions$ = this.#store.pipe(
      select(getSelectedExhibitionId),
      first(
        (selectedExhibitionId) =>
          (has(settings, 'eventId') && settings.eventId === selectedExhibitionId) || isNull(selectedExhibitionId)
      )
    );

    return combineLatest({
      waitForSetOrResetEvenFieldOptions: waitForSetOrResetEvenFieldOptions$,
      isGoogleMapsApiEnabled: this.#store.select(selectIsGoogleMapsApiEnabled),
      titles: this.#store.select(getTitles),
      professions: this.#store.select(getProfessions),
      departments: this.#store.select(getDepartments),
      occupationalGroups: this.#store.select(getOccupationalGroups)
    }).pipe(
      first(
        ({ titles, professions, departments, occupationalGroups }) =>
          !isNull(titles) && !isNull(professions) && !isNull(departments) && !isNull(occupationalGroups)
      ),
      map(({ isGoogleMapsApiEnabled, titles, professions, departments, occupationalGroups }) => {
        const dynamicSavedTicketHolderFormFields = this.createSavedTicketHolderDynamicFormFields(
          formFields,
          isGoogleMapsApiEnabled,
          settings,
          titles,
          professions,
          departments,
          occupationalGroups
        );

        const highestFieldOrder = !isEmpty(dynamicSavedTicketHolderFormFields)
          ? _reduce(dynamicSavedTicketHolderFormFields, (maxOrder, field) => Math.max(maxOrder, field.order), 0)
          : 0;

        const fixedSavedTicketHolderFormFields = this.createFixedSavedTicketTicketHolderDynamicFormFields(
          formFields,
          highestFieldOrder
        );

        const mergedSavedTicketHolderDynamicFormFields = [
          ...dynamicSavedTicketHolderFormFields,
          ...fixedSavedTicketHolderFormFields
        ];

        return sortBy(mergedSavedTicketHolderDynamicFormFields, 'order');
      })
    );
  }

  private createSavedTicketHolderDynamicFormFields(
    formFields: FormFields,
    isGoogleMapsApiEnabled: boolean,
    settings: OperatorsSettingsModel | ExhibitionSettingModel,
    titles: SelectOption[],
    professions: SelectOption[],
    departments: SelectOption[],
    occupationalGroups: SelectOption[]
  ) {
    const {
      ticketOwnerSettings: { fieldSettings },
      isTelephoneValidationDisabled
    } = settings;

    const dynamicFormFields: DynamicFormFields = [];

    const createDynamicFormFieldMapper = this.#dynamicFormFieldBuilder.createDynamicFormFieldMapper();
    const createDynamicFormFieldParametersMapper = savedTicketHolderFormDynamicFormFieldParametersMapper(
      fieldSettings,
      isGoogleMapsApiEnabled,
      isTelephoneValidationDisabled,
      titles,
      professions,
      departments,
      occupationalGroups
    );

    forIn(fieldSettings, (fieldSetting, fieldKey: FieldKey) => {
      const createDynamicFormFieldFunction = createDynamicFormFieldMapper[fieldKey];

      const createDynamicFormFieldParameters = {
        fieldSetting,
        ...createDynamicFormFieldParametersMapper[fieldKey]
      };

      const dynamicFormField: DynamicFormField = createDynamicFormFieldFunction(createDynamicFormFieldParameters);

      if (dynamicFormField) {
        dynamicFormField.isHidden = isFieldHidden(dynamicFormField, formFields);

        dynamicFormFields.push(dynamicFormField);
      }
    });

    return _filter(dynamicFormFields);
  }

  private createFixedSavedTicketTicketHolderDynamicFormFields(formFields: FormFields, order: number) {
    const gdprField = this.#dynamicFormFieldBuilder.createGDPRField({
      checkboxHeading: '',
      checkboxOptionLabel: 'profile.saved-ticket-holders.saved-ticket-holder-form.checkbox.gdpr.label',
      isGDPRConsentChecked: false,
      order: ++order
    });

    const createdDynamicFormFields = _filter([gdprField]);

    forEach(
      createdDynamicFormFields,
      (createdDynamicFormField) =>
        (createdDynamicFormField.isHidden = isFieldHidden(createdDynamicFormField, formFields))
    );

    return createdDynamicFormFields;
  }
}
